import { pendingTask, begin, end } from '../constants/pendingTask';

import * as actions from './actionTypes';
import { fetchFromReitApi as fetch } from './fetchFromApi';
import * as actionHelpers from '../scripts/actionHelpers';
import { download } from '../scripts/fileHelpers';
/**
 * Fetch all the Master Data Management data for a given client id. If successful this will dispatch the LOAD_BULK_PROCESS_MANAGEMENT_DATA_SUCCESS
 * action, otherwise it will dispatch the LOAD_BULK_PROCESS_MANAGEMENT_DATA_FAILURE action.
 * @param {number} clientId The id of the client.
 */
export const fetchGroupsData = (clientId) => {
  return (dispatch) => {
    if (!clientId) {
      return dispatch({
        type: actions.LOAD_MASTER_DATA_MANAGEMENT_DATA_FAILURE,
        groupsData: [],
      });
    }
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    return fetch(`/masterDataManagement?clientId=${clientId}`)
      .then((response) => {
        return response.json();
      })
      .then((groupsData) => {
        if (actionHelpers.isErrorResponse(groupsData)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_MASTER_DATA_MANAGEMENT_DATA_FAILURE,
            null
          );
        }
        dispatch({
          type: actions.LOAD_MASTER_DATA_MANAGEMENT_DATA_SUCCESS,
          groupsData,
          [pendingTask]: end,
        });
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_MASTER_DATA_MANAGEMENT_DATA_FAILURE,
          null,
          error
        );
      });
  };
};

export function deleteEntityGroup(clientId, entityGroupId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/deleteGroup?clientID=${clientId}&entityGroupID=${entityGroupId}`,
      {
        method: 'DELETE',
      }
    )
      .then((response) => {
        return response.text();
      })
      .then((response) => {
        const jsonResponse = response ? JSON.parse(response) : {};
        if (actionHelpers.isErrorResponse(jsonResponse)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.DELETE_GROUP_FAILURE,
            jsonResponse
          );
        }

        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return jsonResponse;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.DELETE_GROUP_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Updates an existing REIT.
 * @param {any} group The REIT
 * @returns {function} A Promise.
 */
export function updateEntityGroup(group) {
  return fetch('/masterDataManagement/update', {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'PUT',
    body: JSON.stringify(group),
  }).then((response) => {
    if (response.ok) {
      return response.json();
    }

    return response.json();
  });
}

export function createGroup(group) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(`/masterDataManagement/addEntityGroup`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(group),
    })
      .then((response) => {
        return response.json();
      })
      .then((group) => {
        if (actionHelpers.isErrorResponse(group)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.CREATE_GROUP_FAILURE,
            group
          );
        }

        dispatch({
          type: actions.CREATE_GROUP_SUCCESS,
          group,
          [pendingTask]: end,
        });
        return group;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.CREATE_GROUP_FAILURE,
          null,
          error
        );
      });
  };
}

export function exportGroupsData(clientId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/exportGroupsData?clientId=${clientId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

/**
 * Fetch property master data for a given client id and optional property master ID.
 * If successful this will dispatch the LOAD_PROPERTY_MASTER_DATA_SUCCESS action,
 * otherwise it will dispatch the LOAD_PROPERTY_MASTER_DATA_FAILURE action.
 * @param {number} clientId The id of the client.
 * @param {number} propertyMasterID Optional property master ID to filter by specific property.
 */
export const fetchPropertyMasterData = (clientId, propertyMasterID = null) => {
  return (dispatch) => {
    if (!clientId) {
      return dispatch({
        type: actions.LOAD_PROPERTY_MASTER_DATA_FAILURE,
        propertyMasterData: [],
      });
    }

    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    let url = `/masterDataManagement/properties?clientId=${clientId}`;
    if (propertyMasterID) {
      url += `&propertyMasterID=${propertyMasterID}`;
    }

    return fetch(url)
      .then((response) => {
        return response.json();
      })
      .then((propertyMasterData) => {
        if (actionHelpers.isErrorResponse(propertyMasterData)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_PROPERTY_MASTER_DATA_FAILURE,
            null
          );
        }

        // Check if propertyMasterID is provided and not null
        // If so, return the single property master data
        if (propertyMasterID && propertyMasterID !== null) {
          return dispatch({
            type: actions.LOAD_SELECTED_PROPERTY_MASTER_DATA_SUCCESS,
            propertyMasterData,
            [pendingTask]: end,
          });
        }

        dispatch({
          type: actions.LOAD_PROPERTY_MASTER_DATA_SUCCESS,
          propertyMasterData,
          [pendingTask]: end,
        });

        return propertyMasterData;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_PROPERTY_MASTER_DATA_FAILURE,
          null,
          error
        );
      });
  };
};

export const fetchTRSData = (clientId) => {
  return (dispatch) => {
    if (!clientId) {
      return dispatch({
        type: actions.LOAD_MASTER_DATA_MANAGEMENT_TRS_DATA_FAILURE,
        trsData: [],
      });
    }
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    return fetch(`/masterDataManagement/getTRSData?clientId=${clientId}`)
      .then((response) => {
        return response.json();
      })
      .then((trsData) => {
        if (actionHelpers.isErrorResponse(trsData)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_MASTER_DATA_MANAGEMENT_TRS_DATA_FAILURE,
            null
          );
        }
        dispatch({
          type: actions.LOAD_MASTER_DATA_MANAGEMENT_TRS_DATA_SUCCESS,
          trsData,
          [pendingTask]: end,
        });
        return trsData;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_MASTER_DATA_MANAGEMENT_TRS_DATA_FAILURE,
          null,
          error
        );
      });
  };
};

export function createPropertyMaster(propertyMaster) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(`/masterDataManagement/addPropertyMaster`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(propertyMaster),
    })
      .then((response) => {
        return response.json();
      })
      .then((propertyMaster) => {
        if (propertyMaster && propertyMaster.errorCode == 'BAD_REQUEST') {
          return dispatch({
            type: actions.CREATE_PROPERTY_MASTER_FAILURE,
            response: propertyMaster,
            [pendingTask]: end,
          });
        } else if (actionHelpers.isErrorResponse(propertyMaster)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.CREATE_PROPERTY_MASTER_FAILURE,
            propertyMaster
          );
        }

        dispatch({
          type: actions.CREATE_PROPERTY_MASTER_SUCCESS,
          propertyMaster,
          [pendingTask]: end,
        });
        return propertyMaster;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.CREATE_PROPERTY_MASTER_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Deletes a property master record.
 * @param {number} propertyMasterId The ID of the property master to delete.
 * @returns {function} A function that returns a Promise.
 */
export function deletePropertyMaster(clientID, reitID, propertyMasterID) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    let url = `/masterDataManagement/deletePropertyMaster?clientID=${clientID}&reitID=${reitID}`;
    if (propertyMasterID) {
      url += `&propertyMasterID=${propertyMasterID}`;
    }

    return fetch(url, { method: 'DELETE' })
      .then((response) => {
        return response.text();
      })
      .then((response) => {
        const jsonResponse = response ? JSON.parse(response) : {};
        if (actionHelpers.isErrorResponse(jsonResponse)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.DELETE_PROPERTY_MASTER_FAILURE,
            jsonResponse
          );
        }

        dispatch({
          type: actions.DELETE_PROPERTY_MASTER_SUCCESS,
          propertyMasterID,
          [pendingTask]: end,
        });
        return jsonResponse;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.DELETE_PROPERTY_MASTER_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Updates an existing property master.
 * @param {object} propertyMaster The property master object to update
 * @returns {function} A thunk function that updates the property master
 */
export function updatePropertyMaster(propertyMaster) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(`/masterDataManagement/updatePropertyMaster`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(propertyMaster),
    })
      .then((response) => {
        return response.json();
      })
      .then((propertyMaster) => {
        if (propertyMaster && propertyMaster.errorCode === 'BAD_REQUEST') {
          return dispatch({
            type: actions.UPDATE_PROPERTY_MASTER_FAILURE,
            response: propertyMaster,
            [pendingTask]: end,
          });
        } else if (actionHelpers.isErrorResponse(propertyMaster)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPDATE_PROPERTY_MASTER_FAILURE,
            propertyMaster
          );
        }

        dispatch({
          type: actions.UPDATE_PROPERTY_MASTER_SUCCESS,
          propertyMaster,
          [pendingTask]: end,
        });
        return propertyMaster;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPDATE_PROPERTY_MASTER_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Clears the property master data from the Redux store
 * @returns {Object} Action to clear property master data
 */
export const clearPropertyMasterData = () => {
  return {
    type: actions.CLEAR_PROPERTY_MASTER_DATA,
  };
};

/**
 * Exports REITs data.
 * @param {number} clientId The ID of the client.
 * @param {boolean} restrictedReportPeriodLimitFlag The restricted report period limit feature flag .
 * @returns {function} A function that returns a Promise.
 */
export function exportREITsData(clientId, restrictedReportPeriodLimitFlag) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/exportREITsData?clientId=${clientId}&restrictedReportPeriodLimitFlag=${restrictedReportPeriodLimitFlag}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

export function uploadGroupTemplate(clientId, file) {
  let formData = new FormData();
  formData.append('clientID', clientId);
  formData.append('file', file);

  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/masterDataManagement/importGroups', {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (actionHelpers.isErrorResponse(response)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_GROUP_FAILURE,
            response
          );
        }
        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPLOAD_GROUP_FAILURE,
          null,
          error
        );
      });
  };
}

export function exportGroupsErrorFile(clientId, fileUploadId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/downloadGroupErrorReport?clientId=${clientId}&fileUploadId=${fileUploadId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}
export function createTRS(trs) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(`/masterDataManagement/addTRS`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(trs),
    })
      .then((response) => {
        return response.json();
      })
      .then((trs) => {
        if (trs && trs.errorCode == 'BAD_REQUEST') {
          return dispatch({
            type: actions.CREATE_TRS_FAILURE,
            response: trs,
            [pendingTask]: end,
          });
        } else if (actionHelpers.isErrorResponse(trs)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.CREATE_TRS_FAILURE,
            trs
          );
        }

        dispatch({
          type: actions.CREATE_TRS_SUCCESS,
          trs,
          [pendingTask]: end,
        });
        return trs;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.CREATE_TRS_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Uploads REITs data.
 * @param {number} clientId The ID of the client.
 * @param {File} file The REIT Template file.
 * @returns {function} A function that returns a Promise.
 */
export function uploadREITTemplate(clientId, file) {
  let formData = new FormData();
  formData.append('clientID', clientId);
  formData.append('file', file);

  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/masterDataManagement/importREITs', {
      method: 'POST',
      body: formData,
    })
      .then((response) => response.text())
      .then((responseText) => {
        let jsonResponse;
        try {
          jsonResponse = responseText ? JSON.parse(responseText) : {};
        } catch (e) {
          // Check if the response is an IIS timeout HTML page
          const isIISTimeoutResponse =
            responseText && responseText.includes('The request timed out');
          const errorMessage = isIISTimeoutResponse
            ? 'Upload failed due to a timeout. Please try the upload again.'
            : 'Upload failed due to an unexpected error. Please try the upload again.';
          const parseError = new Error(errorMessage);
          parseError.isIISTimeout = isIISTimeoutResponse;
          throw parseError;
        }

        if (actionHelpers.isErrorResponse(jsonResponse)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_REIT_FAILURE,
            jsonResponse
          );
        }
        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return jsonResponse;
      })
      .catch((error) => {
        if (error.isIISTimeout) {
          // End the task without dispatching UPLOAD_REIT_FAILURE
          // so the global error banner is not shown for IIS timeout responses
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        } else {
          actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_REIT_FAILURE,
            null,
            error
          );
        }
        return Promise.reject(error);
      });
  };
}

/**
 * Exports REITs error file.
 * @param {number} clientId The ID of the client.
 * @param {number} fileUploadId The ID of file upload.
 * @returns {function} A function that returns a Promise.
 */
export function exportREITsErrorFile(clientId, fileUploadId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/downloadREITErrorReport?clientId=${clientId}&fileUploadId=${fileUploadId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

export function exportAllTypesData(clientId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(`/masterDataManagement/allTypeExport?clientId=${clientId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

export function updateTRS(trsData) {
  return fetch('/masterDataManagement/UpdateTRS', {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'PUT',
    body: JSON.stringify(trsData),
  }).then((response) => {
    if (response.ok) {
      return response.json();
    }
    return response.json();
  });
}

export function deleteTRS(clientId, reitId, taxableREITSubsidiaryMasterId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/deleteTRS?clientID=${clientId}&reitID=${reitId}&taxableREITSubsidiaryMasterId=${taxableREITSubsidiaryMasterId}`,
      {
        method: 'DELETE',
      }
    )
      .then((response) => {
        return response.text();
      })
      .then((response) => {
        const jsonResponse = response ? JSON.parse(response) : {};
        if (actionHelpers.isErrorResponse(jsonResponse)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.DELETE_MDM_TRS_FAILURE,
            jsonResponse
          );
        }

        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return jsonResponse;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.DELETE_MDM_TRS_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Fetch security master data for a given client id and optional security master ID.
 * If successful this will dispatch the LOAD_SECURITY_MASTER_DATA_SUCCESS action,
 * otherwise it will dispatch the LOAD_SECURITY_MASTER_DATA_FAILURE action.
 * @param {number} clientId The id of the client.
 * @param {number} securityMasterID Optional security master ID to filter by specific security.
 */
export const fetchSecurityMasterData = (clientId, securityMasterID = null) => {
  return (dispatch) => {
    if (!clientId) {
      return dispatch({
        type: actions.LOAD_SECURITY_MASTER_DATA_FAILURE,
        securityMasterData: [],
      });
    }

    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    let url = `/masterDataManagement/securities?clientId=${clientId}`;
    if (securityMasterID) {
      url += `&securityMasterID=${securityMasterID}`;
    }

    return fetch(url)
      .then((response) => {
        return response.json();
      })
      .then((securityMasterData) => {
        if (actionHelpers.isErrorResponse(securityMasterData)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_SECURITY_MASTER_DATA_FAILURE,
            null
          );
        }
        dispatch({
          type: actions.LOAD_SECURITY_MASTER_DATA_SUCCESS,
          securityMasterData,
          [pendingTask]: end,
        });

        return securityMasterData;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_SECURITY_MASTER_DATA_FAILURE,
          null,
          error
        );
      });
  };
};

export function createSecurity(security) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(`/masterDataManagement/addSecurityMaster`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(security),
    })
      .then((response) => {
        return response.json();
      })
      .then((security) => {
        if (actionHelpers.isErrorResponse(security)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.CREATE_SECURITY_FAILURE,
            security
          );
        }

        dispatch({
          type: actions.CREATE_SECURITY_SUCCESS,
          security,
          [pendingTask]: end,
        });
        return security;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.CREATE_SECURITY_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Updates an existing Security.
 * @param {any} security The Security
 * @returns {function} A Promise.
 */
export function updateSecurity(securityMaster) {
  return fetch('/masterDataManagement/updateSecurity', {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'PUT',
    body: JSON.stringify(securityMaster),
  }).then((response) => {
    if (response.ok) {
      return response.json();
    }
    return response.json();
  });
}

export function deleteSecurity(clientId, reitid, securityMasterID) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/deleteSecurity?clientID=${clientId}&reitID=${reitid}&securitiesMasterID=${securityMasterID}`,
      {
        method: 'DELETE',
      }
    )
      .then((response) => {
        return response.text();
      })
      .then((response) => {
        const jsonResponse = response ? JSON.parse(response) : {};
        if (actionHelpers.isErrorResponse(jsonResponse)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.DELETE_SECURITY_MASTER_DATA_FAILURE,
            jsonResponse
          );
        }

        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return jsonResponse;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.DELETE_SECURITY_MASTER_DATA_FAILURE,
          null,
          error
        );
      });
  };
}

export function uploadSecurityTemplate(clientId, file) {
  let formData = new FormData();
  formData.append('clientID', clientId);
  formData.append('file', file);

  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/masterDataManagement/importSecurities', {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (actionHelpers.isErrorResponse(response)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_SECURITY_FAILURE,
            response
          );
        }
        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPLOAD_SECURITY_FAILURE,
          null,
          error
        );
      });
  };
}

export function exportSecuritiesErrorFile(clientId, fileUploadId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/downloadSecurityErrorReport?clientId=${clientId}&fileUploadId=${fileUploadId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename); // Assuming 'download' is a globally available function or imported
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

/**
 * Exports properties data.
 * @param {number} clientId The ID of the client.
 * @returns {function} A function that returns a Promise.
 */
export function exportPropertiesData(clientId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/exportPropertiesData?clientId=${clientId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

/**
 * Clears the end date of property master records and creates property records in future reporting periods.
 * @param {number} clientID The ID of the client.
 * @param {number} propertyMasterID The ID of the property master.
 * @returns {function} A function that returns a Promise.
 */
export function clearPropertyMasterEndDate(clientID, propertyMasterID, reitID) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    // Ensure propertyMasterID is always an array
    const propertyMasterIDs = [propertyMasterID];

    return fetch('/masterDataManagement/clearPropertyMasterEndDate', {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        clientID,
        propertyMasterIDs,
        reitID,
      }),
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (
          actionHelpers.isErrorResponse(response) ||
          response.errorMessage != ''
        ) {
          response.message =
            response.errorMessage != '' ? response.errorMessage : null;
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPDATE_PROPERTY_MASTER_FAILURE,
            response
          );
        }

        dispatch({
          type: actions.UPDATE_PROPERTY_MASTER_SUCCESS,
          propertyMaster: response,
          [pendingTask]: end,
        });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPDATE_PROPERTY_MASTER_FAILURE,
          null,
          error
        );
      });
  };
}

export function uploadPropertyMasterTemplate(clientId, file) {
  let formData = new FormData();
  formData.append('clientID', clientId);
  formData.append('file', file);

  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/masterDataManagement/importProperties', {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (actionHelpers.isErrorResponse(response)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_PROPERTY_MASTER_FAILURE,
            response
          );
        }
        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPLOAD_PROPERTY_MASTER_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Uploads properties data.
 * @param {number} clientId The ID of the client.
 * @param {File} file The properties template file.
 * @returns {function} A function that returns a Promise.
 */
export function uploadPropertiesTemplate(clientId, file) {
  let formData = new FormData();
  formData.append('clientID', clientId);
  formData.append('file', file);

  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/masterDataManagement/importProperties', {
      method: 'POST',
      body: formData,
    })
      .then((response) => response.json())
      .then((response) => {
        if (actionHelpers.isErrorResponse(response)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_PROPERTIES_FAILURE,
            response
          );
        }
        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPLOAD_PROPERTIES_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Exports properties error file.
 * @param {number} clientId The ID of the client.
 * @param {number} fileUploadId The ID of file upload.
 * @returns {function} A function that returns a Promise.
 */
export function exportPropertiesErrorFile(clientId, fileUploadId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/downloadPropertyErrorReport?clientId=${clientId}&fileUploadId=${fileUploadId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

/**
 * Exports TRS data.
 * @param {number} clientId The ID of the client.
 * @param {boolean} restrictedReportPeriodLimitFlag The restricted report period limit feature flag .
 * @returns {function} A function that returns a Promise.
 */
export function exportTRSData(clientId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(`/masterDataManagement/exportTRSData?clientId=${clientId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

/**
 * Ends a property by setting its end date and removing future period records.
 * @param {number} reitID The ID of the REIT.
 * @param {number} propertyMasterID The ID of the property master.
 * @param {Date} endDate The end date for the property.
 * @param {number} clientID The ID of the client.
 * @returns {function} A function that returns a Promise.
 */
export function endProperty(reitID, propertyMasterID, endDate, clientID) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/masterDataManagement/endProperty', {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        REITID: reitID,
        PropertyMasterID: propertyMasterID,
        EndDate: endDate,
        ClientID: clientID,
      }),
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (response && response.errorMessage != '') {
          return dispatch({
            type: actions.UPDATE_PROPERTY_MASTER_FAILURE,
            response,
            [pendingTask]: end,
          });
        } else if (actionHelpers.isErrorResponse(response)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPDATE_PROPERTY_MASTER_FAILURE,
            response
          );
        }

        dispatch({
          type: actions.UPDATE_PROPERTY_MASTER_SUCCESS,
          propertyMaster: response,
          [pendingTask]: end,
        });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPDATE_PROPERTY_MASTER_FAILURE,
          null,
          error
        );
      });
  };
}

export function exportSecuritiesData(clientId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/exportSecuritiesData?clientId=${clientId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

/**
 * Uploads TRS data.
 * @param {number} clientId The ID of the client.
 * @param {File} file The TRS template file.
 * @returns {function} A function that returns a Promise.
 */
export function uploadTRSMasterTemplate(clientId, file) {
  let formData = new FormData();
  formData.append('clientID', clientId);
  formData.append('file', file);

  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/masterDataManagement/importTRS', {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (actionHelpers.isErrorResponse(response)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_PROPERTY_MASTER_FAILURE,
            response
          );
        }
        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPLOAD_PROPERTY_MASTER_FAILURE,
          null,
          error
        );
      });
  };
}

/**
 * Exports TRS error file.
 * @param {number} clientId The ID of the client.
 * @param {number} fileUploadId The ID of file upload.
 * @returns {function} A function that returns a Promise.
 */
export function exportTRSErrorFile(clientId, fileUploadId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/masterDataManagement/downloadTRSErrorReport?clientId=${clientId}&fileUploadId=${fileUploadId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}
